import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import { Box, CircularProgress, Typography } from "@mui/material";
import LOGO_BASE64 from "../assets/data/logoBase64";
import dayjs from "dayjs";
import { calculateTotals } from "../utils/calculateTotals";
import { buildBalanceSheetHtml } from "../utils/balanceSheetHtml";
import { getTrays } from "../actions";

const TransactionViewer = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const initialized = useRef(false);

    const customerId = searchParams.get("customerId");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    useEffect(() => {
        // Prevent double execution in Strict Mode
        if (!customerId || initialized.current) return;
        
        initialized.current = true;
        fetchAndPrint();
    }, [customerId]);

    const fetchAndPrint = async () => {
        try {
            setLoading(true);

            const payload = { customerId };
            if (startDate && endDate) {
                payload.startDate = dayjs(startDate).format("YYYY-MM-DD");
                payload.endDate = dayjs(endDate).format("YYYY-MM-DD");
            }

            const [custRes, txRes, allTxRes, traysList] = await Promise.all([
                axiosInstance.post("/customers/details", { customerId }),
                axiosInstance.post("/transactions/list", payload),
                (startDate && endDate)
                    ? axiosInstance.post("/transactions/list", { customerId })
                    : Promise.resolve(null),
                getTrays(),
            ]);

            const customerDetails = custRes?.data?.result || {};
            const transactionList = txRes?.data?.result || [];
            // Tray prices live on the tray types, not on the customer's tray balance
            const trayTypes =
                Array.isArray(traysList) && traysList.length > 0
                    ? traysList
                    : Array.isArray(customerDetails?.trayBalance)
                    ? customerDetails.trayBalance
                    : [];
            const allTransactions = allTxRes?.data?.result || transactionList;

            if (transactionList.length === 0) {
                toast.info("No transactions found for the selected period.");
                setLoading(false);
                return;
            }

            const sortedTransactionList = [...transactionList].sort(
                (a, b) => {
                    const dateDifference =
                        new Date(a.dateOfTransaction).getTime() -
                        new Date(b.dateOfTransaction).getTime();
                    if (dateDifference) return dateDifference;

                    const timeDifference =
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime();
                    if (Number.isFinite(timeDifference) && timeDifference) {
                        return timeDifference;
                    }

                    return String(a._id || "").localeCompare(String(b._id || ""));
                }
            );
            const totals = calculateTotals(transactionList);
            const { 
              grandTotalTransactionAmount,
              discountAmount,
              labourCharges,
              grandTotalPaidAmount,
              damageRefund,
              grandTrayPaymentAmount,
              totalPaid,
              totalDueAmount
            } = totals;

            generatePrint(
                sortedTransactionList,
                customerDetails,
                trayTypes,
                {
                    grandTotalTransactionAmount,
                    discountAmount,
                    labourCharges,
                    grandTotalPaidAmount,
                    grandTrayPaymentAmount,
                    damageRefund,
                    totalPaid,
                    totalDueAmount
                },
                startDate,
                endDate,
                allTransactions
            );

        } catch (err) {
            console.error(err);
            toast.error("Failed to load transaction data");
        } finally {
            setLoading(false);
        }
    };

    const generatePrint = (list, customer, trayTypes, totals, sDate, eDate, allTransactions = []) => {
        window.document.write(
            buildBalanceSheetHtml({
                title: `Transactions - ${customer?.name || ""}`,
                logo: LOGO_BASE64,
                employerName: customer?.employerDetails?.employerName || "-",
                branchName: customer?.branchName || "-",
                customer,
                transactions: list,
                allTransactions,
                trayTypes,
            })
        );

        window.document.close();
        window.focus();
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
            {loading ? (
                <Box textAlign="center">
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Preparing your document...</Typography>
                </Box>
            ) : (
                <Typography>Print session finished. You can close this tab.</Typography>
            )}
        </Box>
    );
};

export default TransactionViewer;
